<template>
  <div v-loading="form.processing" class="user-form">
    <form @submit.prevent="handleFormSubmit">
      <form-group
        v-if="requireOrganization"
        id="user-organization"
        label="Organizaciones"
        :errors="form.errors">
        <el-select
          v-model="form.data.organizations"
          remote
          multiple
          filterable
          clearable
          :remote-method="fetchOrganizations"
          :loading="fetchingOrganizations"
          @change="handleOrganizationSelect">
          <el-option
            v-for="organization in availableOrganizations"
            :key="organization.uuid"
            :value="organization.uuid"
            :label="getOrganizationLabel(organization)" />
        </el-select>
      </form-group>
      <form-group
        v-if="showUserSearch"
        label="Buscar usuarios aprobados">
        <el-select
          v-model="selectedRemoteUserRut"
          v-loading="fetchingOrganizationRemoteUsers"
          element-loading-text="Cargando usuarios aprobados..."
          filterable
          clearable
          placeholder="Selecciona un usuario"
          @change="handleRemoteUserSelect">
          <el-option
            v-for="remoteUser in organizationRemoteUsers"
            :key="remoteUser.email"
            :label="`${remoteUser.given_name} ${remoteUser.family_name} - ${remoteUser.organization_uuid}`"
            :value="remoteUser.claveunica_id" />
        </el-select>
      </form-group>
      <form-group
        id="user-rut"
        label="Rut del usuario"
        :errors="form.errors">
        <app-rut-input
          ref="rutInput"
          v-model="form.data.rut"
          class="form-control"
          type="text"
          :readonly="disableRutChange"
          @input="handleRutChange" />
      </form-group>
      <form-group
        v-if="shouldShowUserType"
        id="user-type"
        label="Tipo"
        :errors="form.errors">
        <el-select
          v-model="form.data.type">
          <el-option
            v-for="userType in allowedUserTypes"
            :key="userType.type"
            :value="userType.type"
            :label="userType.name" />
        </el-select>
      </form-group>
      <div v-loading="fetchingUserDetails" element-loading-text="Obteniendo información básica del usuario">
        <form-group id="user-given_name" label="Nombres" :errors="form.errors">
          <input
            v-model="form.data.given_name"
            :readonly="remoteUserValidated"
            class="form-control"
            type="text">
        </form-group>
        <form-group id="user-family_name" label="Apellidos" :errors="form.errors">
          <input
            v-model="form.data.family_name"
            :readonly="remoteUserValidated"
            class="form-control"
            type="text">
        </form-group>
        <form-group id="user-email" label="E-mail" :errors="form.errors">
          <input
            v-model="form.data.email"
            class="form-control"
            type="email">
        </form-group>
      </div>
      <button class="btn btn-primary" :disabled="fetchingFormData">
        Guardar
      </button>
    </form>
  </div>
</template>

<script>
import _debounce from 'lodash/debounce';
import FormGroup from '@/components/FormGroup';
import ErrorHelper from '@/helpers/error';
import _uniqBy from 'lodash/uniqBy';
import _intersection from 'lodash/intersection';
import AppRutInput from '@/components/AppRutInput';
import userTypes from '@/assets/constants/user-roles';

export default {
  name: 'UserForm',
  components: {
    FormGroup,
    AppRutInput
  },
  props: {
    type: {
      type: String,
      required: true
    },
    user: {
      type: Object,
      default() {
        return {};
      }
    },
    defaultOrganization: {
      type: Object,
      default() {
        return {};
      }
    }
  },
  data() {
    return {
      form: {
        data: Object.assign({
          organizations: []
        }, this.user, {
          organizations: this.user.organizations.length ? this.user.organizations.map(u => u.uuid) : []
        }),
        errors: {},
        processing: false
      },
      selectedRemoteUserRut: null,
      remoteUserValidated: false,
      organizations: Object.keys(this.defaultOrganization).length ? [JSON.parse(JSON.stringify(this.defaultOrganization))] : [],
      organizationRemoteUsers: [],
      fetchingUserDetails: false,
      fetchingOrganizationRemoteUsers: false,
      fetchingOrganizationUsers: false,
      fetchingOrganizations: false
    };
  },
  computed: {
    disableRutChange() {
      return this.user.id;
    },
    shouldShowUserType() {
      return !this.type;
    },
    availableOrganizations() {
      return _uniqBy([...this.organizations, ...this.user.organizations], 'uuid').filter(o => o.name);
    },
    organizationsSource() {
      return ['dispenser', 'dispenser_admin'].includes(this.type) ? 'midas' : 'medical_center';
    },
    fetchingFormData() {
      return this.fetchingUserDetails || this.fetchingOrganizationRemoteUsers || this.fetchingOrganizationUsers || this.fetchingOrganizations;
    },
    requireOrganization() {
      return ['dispenser_admin', 'dispenser', 'medical_center'].includes(this.type);
    },
    selectedOrganizations() {
      return this.organizations.filter(o => this.form.data.organizations.includes(o.uuid));
    },
    restrictOrganization() {
      return this.type === 'dispenser';
    },
    showUserSearch() {
      return this.type === 'dispenser_admin' && process.env.VUE_APP_ENABLE_MIDAS_USER_SEARCH === 'true';
    },
    isRemoteUserRut() {
      const rut = this.form.data.rut ? this.form.data.rut.replace(/[^kK0-9]/, '') : '';
      return this.organizationRemoteUsers.find(o => o.claveunica_id.replace(/[^kK0-9]/, '') === rut);
    },
    allowedUserTypes() {
      return userTypes.filter(t => {
        if (!t.organizations || (t.remoteOnly && !this.isRemoteUserRut)) {
          return false;
        }
        return _intersection(t.organizations, this.selectedOrganizations.map(s => s.source)).length;
      });
    }
  },
  mounted() {
    this.$refs.rutInput && this.$refs.rutInput.focus();
    this.fetchOrganizations();
    if (this.user.id) {
      this.fetchUserDetails(this.user.claveunica_id, false);
    }
  },
  methods: {
    getOrganizationLabel(organization) {
      
      if (organization.source === 'midas') {
        return `${organization.name} - [${organization.code}] - Local: ${organization.local}`;
      }
      return `${organization.name} - [${organization.rut}] - Local: ${organization.local}`;
    },
    fetchOrganizations(searchQuery) {
      this.fetchingOrganizations = true;
      return this.$store.dispatch('fetchOrganizations', { source: this.organizationsSource, query: searchQuery, status: 'active' })
        .then(organizations => {
          this.organizations = organizations.data;
        })
        .finally(() => {
          this.fetchingOrganizations = false;
        });
    },
    fetchOrganizationRemoteUsers(organizationUuids) {
      this.fetchingOrganizationRemoteUsers = true;
      this.$store.dispatch('fetchOrganizationRemoteUsers', {
        uuids: organizationUuids
      }).then(users => {
        this.organizationRemoteUsers = users;
      }).finally(() => {
        this.fetchingOrganizationRemoteUsers = false;
      });
    },
    handleFormSubmit() {
      if (this.fetchingFormData) return false;
      const formData = Object.assign({
        type: this.form.data.type || this.type
      }, this.form.data);

      this.form.processing = true;
      this.form.errors = {};

      this.$store.dispatch('saveUser', formData)
        .then(user => {
          this.$emit('user-saved', user);
        })
        .catch(e => {
          this.form.errors = ErrorHelper.mapErrorFields(e.response.data, {
            rut: 'user-rut',
            organizations: 'user-organization',
            email: 'user-email',
            family_name: 'user-family_name',
            given_name: 'user-given_name'
          });
        })
        .finally(() => {
          this.form.processing = false;
        });
    },
    fetchUserDetails(rut, overrideWithResponse = true) {
      this.fetchingUserDetails = true;
      this.form.errors = {};
      this.remoteUserValidated = false;
      this.$store.dispatch('fetchUserDetails', { rut: rut, type: this.type })
        .then(response => {
          if (overrideWithResponse) {
            this.form.data.given_name = response.given_name;
            this.form.data.family_name = response.family_name;
          }
          this.remoteUserValidated = response.validated;
        })
        .catch(e => {
          if (overrideWithResponse) {
            this.form.data.given_name = '';
            this.form.data.family_name = '';
            this.form.errors = ErrorHelper.mapErrorFields(e.response.data, { rut: 'user-rut', organization: 'user-organization' });
          }
        })
        .finally(() => {
          this.fetchingUserDetails = false;
        });
    },
    handleOrganizationSelect(organizationUuids) {
      if (this.showUserSearch) {
        this.fetchOrganizationRemoteUsers(organizationUuids);
      }
    },
    handleRemoteUserSelect(remoteUserRut) {
      const remoteUser = this.organizationRemoteUsers.find(u => u.claveunica_id === remoteUserRut);
      this.form.errors = {};
      this.form.data = {
        ...this.form.data,
        ...remoteUser,
        rut: remoteUser.claveunica_id
      };
      this.form.data.type = 'dispenser_admin';
    },
    handleRutChange: _debounce(function(value) {
      this.fetchUserDetails(value);
    }, 200)
  }
};
</script>

<style lang="scss">
.user-form {
  form {
    display: flex;
    flex-direction: column;

    button {
      align-self: end;
    }
  }
}
</style>
